import { AuditResponse, AuditPageable } from 'src/Views';
import { FLOW_SERVICE_BASE_URL, FLOW_HEADERS, AIPDATA_BASE_URL } from '../../ApiConfig';
import { apiClient } from '../services/apiClient';
import { azureApiClient } from '../services/azureApiClient';


export function getEnvironmentParams(): { portalEnv: string; iamEnv: string } {
  try {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const encodedData = urlSearchParams.get('data') || '';
    const decodedData = encodedData ? atob(encodedData) : '';
    const urlParams = new URLSearchParams(decodedData);
    
    return {
      portalEnv: urlParams.get('portalEnv') || '',
      iamEnv: urlParams.get('iamEnv') || '',
    };
  } catch (error) {
    console.error('Error decoding environment parameters:', error);
    return { portalEnv: '', iamEnv: '' };
  }
}


export interface UserRoleResponse {
  id: number;
  username: string;
  name: string;
  isAdmin: boolean;
  roleCode: 'Editor' | 'Reader' | 'None';
  environment: string;
}


function isExchangeTokenFlow(): boolean {
  return azureApiClient.hasRefreshCapability();
}


async function checkUserHasEditorRole(): Promise<boolean> {
  // Skip role check for check-token flow
  if (!isExchangeTokenFlow()) {
    console.log('Check-token flow detected, skipping role verification');
    return true;
  }

  try {
    const userInfo = azureApiClient.getUserInfo();
    if (!userInfo || !userInfo.email) {
      throw new Error('User information not available');
    }

    const { portalEnv, iamEnv } = getEnvironmentParams();

    const params = new URLSearchParams({
      username: userInfo.email,
      portalEnv: portalEnv,
      iamEnv: iamEnv,
    });

    const response = await azureApiClient.fetch(`/v2/atlas/user-role?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user role: ${response.statusText}`);
    }

    const userRole: UserRoleResponse = await response.json();
    return userRole.roleCode === 'Editor';
  } catch (error) {
    console.error('Error checking user role:', error);
    throw new Error('Failed to verify user permissions. Please try again.');
  }
}


async function verifyEditorRole(): Promise<void> {
  // Skip role check for check-token flow
  if (!isExchangeTokenFlow()) {
    console.log('Check-token flow: Role verification skipped');
    return;
  }

  const hasEditorRole = await checkUserHasEditorRole();
  
  if (!hasEditorRole) {
    throw new Error(
      'Access Denied: You do not have sufficient permissions to perform this action. Editor role is required.',
    );
  }
}

export async function fetchTemplate(queryParam: string) {
  try {
    const response = await apiClient.fetch(
      `${FLOW_SERVICE_BASE_URL}templates/check${queryParam}`,
      {
        method: 'GET',
        headers: {
          'x-aptean-tenant': FLOW_HEADERS['x-aptean-tenant'],
        },
      },
    );
    return response.json();
  } catch (error) {
    throw error;
  }
}

export async function fetchTemplateFile(
  templateid: string,
  sourceSchema: string,
  targetSchema: string,
) {
  try {
    const response = await apiClient.fetch(
      `${FLOW_SERVICE_BASE_URL}templates/${templateid}`,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-aptean-tenant': FLOW_HEADERS['x-aptean-tenant'],
        },
        body: JSON.stringify({
          sourceSchema: sourceSchema,
          targetSchema: targetSchema,
        }),
      },
    );
    return response.blob();
  } catch (error) {
    throw error;
  }
}

export async function saveTemplate(formData: FormData, queryParam?: string) {
  // Verify user has Editor role (only for exchange-token flow)
  await verifyEditorRole();

  try {
    const response = await apiClient.fetch(
      `${FLOW_SERVICE_BASE_URL}templates${queryParam}`,
      {
        method: 'POST',
        headers: {
          'x-aptean-tenant': FLOW_HEADERS['x-aptean-tenant'],
        },
        body: formData,
      },
    );
    return response.json();
  } catch (error) {
    throw error;
  }
}

export function deleteTemplate(templateId: string): Promise<any> {
  return apiClient
    .fetch(`${FLOW_SERVICE_BASE_URL}templates/${templateId}`, {
      method: 'DELETE',
      headers: {
        ...FLOW_HEADERS,
      },
    })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to delete template: ${response.statusText}`);
      }
      return response.json();
    })
    .catch((error) => {
      console.error('Error deleting template:', error);
      throw error;
    });
}

export async function transformData(formData: FormData) {
  try {
    const response = await apiClient.fetch(
      `${FLOW_SERVICE_BASE_URL}transformation/preview`,
      {
        method: 'POST',
        headers: {
          'x-aptean-tenant': FLOW_HEADERS['x-aptean-tenant'],
        },
        body: formData,
      },
    );
    return response.json();
  } catch (error) {
    throw error;
  }
}

export async function fetchAuditsByEntity(
  entityId: string,
  pageable: AuditPageable,
): Promise<AuditResponse> {
  const response = await apiClient.fetch(
    `${FLOW_SERVICE_BASE_URL}audits/entity/${entityId}?page=${pageable.page}&size=${pageable.size}&sort=${pageable.sort.join(',')}`,
    {
      method: 'GET',
      headers: {
        'x-aptean-tenant': FLOW_HEADERS['x-aptean-tenant'],
      },
    },
  );

  if (response.status === 404) {
    return {
      status: 'success',
      message: 'No audit logs found',
      data: {
        content: [],
        pageNumber: pageable.page,
        pageSize: pageable.size,
        totalElements: 0,
        totalPages: 0,
      },
      timestamp: new Date().toISOString(),
      errors: null,
      success: true,
      executionTimeMillis: 0,
    };
  }

  if (!response.ok) {
    throw new Error(`fetchAuditsByEntity failed: ${response.statusText}`);
  }
  return response.json() as Promise<AuditResponse>;
}

export async function fetchTenantNames(
  ids: string[],
  headerTenantId: string,
): Promise<Record<string, string>> {
  if (!ids.length) {
    return {};
  }

  const params = new URLSearchParams();
  for (const id of ids) {
    params.append('id', id);
  }
  const url = `${AIPDATA_BASE_URL}/v2/producers?${params.toString()}`;

  const resp = await apiClient.fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-aptean-tenant': headerTenantId,
    },
  });
  if (!resp.ok) {
    throw new Error(
      `fetchTenantNames failed: ${url} ${resp.status} ${resp.statusText}`,
    );
  }

  const body = await resp.json();
  const items: Array<{ id: string; name: string }> = body.items || [];
  return items.reduce((map, t) => {
    map[t.id] = t.name;
    return map;
    }, {} as Record<string, string>);
}

// Export helper functions for UI usage
export { checkUserHasEditorRole, isExchangeTokenFlow };
